/* ============================================================
   H2Glow -- Bundle Builder
   <h2g-bundle-builder> owns tier + flavor + plan selection and
   writes the resulting cart lines into the theme's existing
   product form, so the theme's own <product-form> handles the
   POST, the cart drawer, section rendering and error states.

   No third-party JS. Markup is server-rendered in the correct
   default state, so this script only handles change events.
   ============================================================ */

if (!customElements.get("h2g-bundle-builder")) {
  customElements.define(
    "h2g-bundle-builder",
    class H2gBundleBuilder extends HTMLElement {
      connectedCallback() {
        const raw = this.querySelector('[data-h2g-config]');
        if (!raw) return;

        try {
          this.config = JSON.parse(raw.textContent);
        } catch (e) {
          console.error("[h2g-bundle-builder] bad config JSON", e);
          return;
        }

        this.variantsById = new Map(
          this.config.variants.map((v) => [String(v.id), v])
        );
        this.tiersByQty = new Map(
          this.config.tiers.map((t) => [Number(t.qty), t])
        );

        this.form =
          document.getElementById(this.config.formId) ||
          document.querySelector('form[data-type="add-to-cart-form"]');

        this.lineHolder = document.createElement("div");
        this.lineHolder.hidden = true;
        this.lineHolder.setAttribute("data-h2g-lines", "");
        this.form?.appendChild(this.lineHolder);

        this.addEventListener("change", this.onChange.bind(this));

        // Appstle's subscription JS injects its own selling_plan inputs
        // after load; strip them at the last moment before submit.
        // Capture phase on document runs before the theme's own handler.
        this.onDocSubmit = (evt) => {
          if (this.form && evt.target === this.form) {
            this.writeCartLines(this.tierQty);
          }
        };
        document.addEventListener("submit", this.onDocSubmit, true);

        // During the sale one-time is the better price, so it starts selected.
        if (window.h2gSaleOn && window.h2gSaleOn() && window.h2gSaleOff(this.config.defaultVariantId)) {
          const once = this.querySelector('[data-h2g-plan][value]:not([value="subscribe"])');
          if (once) once.checked = true;
        }

        this.sync();
        if (this.config.soldOut) this.setupSoldOut();
      }

      /* ---------- sold out ---------- */

      setupSoldOut() {
        const submit = this.form?.querySelector('[type="submit"]');
        if (submit) {
          submit.disabled = true;
          submit.setAttribute("aria-disabled", "true");
          const label = submit.querySelector("span") || submit;
          label.textContent = "Sold out";
        }

        const notify = this.querySelector("[data-h2g-notify]");
        if (!notify) return;
        this.notifyForm = notify;
        if (this.form) this.form.insertAdjacentElement("afterend", notify);

        const input = notify.querySelector('input[type="email"]');
        const button = notify.querySelector('button[type="submit"]');
        const msg = notify.querySelector("[data-h2g-notify-msg]");

        const say = (text, isError) => {
          msg.hidden = false;
          msg.textContent = text;
          msg.classList.toggle("is-error", !!isError);
        };

        notify.addEventListener("submit", (evt) => {
          evt.preventDefault();
          const email = input.value.trim();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            say("Enter an email address like name@example.com.", true);
            input.focus();
            return;
          }
          button.disabled = true;
          fetch(
            "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" +
              encodeURIComponent(this.config.klaviyoCompanyId),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/vnd.api+json",
                revision: "2024-10-15",
              },
              body: JSON.stringify({
                data: {
                  type: "back-in-stock-subscription",
                  attributes: {
                    channels: ["EMAIL"],
                    profile: { data: { type: "profile", attributes: { email } } },
                  },
                  relationships: {
                    variant: {
                      data: {
                        type: "catalog-variant",
                        id: "$shopify:::$default:::" + this.config.notifyVariantId,
                      },
                    },
                  },
                },
              }),
            }
          )
            .then((res) => {
              if (res.ok) {
                notify.querySelector(".h2g-bb__notify-row").hidden = true;
                say("You\u2019re on the list. We\u2019ll email you when it\u2019s back.");
                return;
              }
              return res.json().then((body) => {
                const detail = body?.errors?.[0]?.detail;
                say((detail ? detail + ". " : "") + "Check the address and try again.", true);
                button.disabled = false;
              });
            })
            .catch(() => {
              say("Couldn\u2019t reach the sign-up service. Try again in a moment.", true);
              button.disabled = false;
            });
        });
      }

      /* ---------- state ---------- */

      get planMode() {
        return this.querySelector("[data-h2g-plan]:checked")?.value || "onetime";
      }

      get planId() {
        if (this.planMode !== "subscribe") return null;
        const sel = this.querySelector("[data-h2g-frequency]");
        return sel ? String(sel.value) : null;
      }

      get tierQty() {
        const checked = this.querySelector(".h2g-bb__tier-input:checked");
        return checked ? Number(checked.value) : 1;
      }

      /** Variant ids chosen for the active tier, one per bag. */
      selectedVariantIds(qty) {
        const tier = this.querySelector(
          `[data-h2g-tier="${qty}"]`
        );
        if (!tier) return [];
        return Array.from(
          { length: qty },
          (_, i) =>
            tier.querySelector(`[data-h2g-flavor="${qty}-${i}"]:checked`)
              ?.value || String(this.config.defaultVariantId)
        );
      }

      /* ---------- pricing ---------- */

      /** Unit price for one bag, in cents, under the current plan + tier. */
      unitPrice(variantId, planId, perUnitOff) {
        const v = this.variantsById.get(String(variantId));
        if (!v) return 0;
        const base = planId && v.plans && v.plans[planId] != null
          ? v.plans[planId]
          : v.price;
        const saleOff = !planId && window.h2gSaleOff ? window.h2gSaleOff(variantId) : 0;
        return Math.max(0, base - (perUnitOff || 0) - saleOff);
      }

      /** Full price for one bag (what the strikethrough shows). */
      comparePrice(variantId) {
        const v = this.variantsById.get(String(variantId));
        if (!v) return 0;
        return v.compareAt || v.price;
      }

      /**
       * Totals for a tier. Flavors only matter for the selected tier;
       * unselected tiers price at the default variant so the ladder
       * stays comparable.
       */
      totalsFor(qty, variantIds, planIdOverride) {
        const tier = this.tiersByQty.get(qty) || { perUnitOff: 0 };
        const planId = planIdOverride === undefined ? this.planId : planIdOverride;
        const ids =
          variantIds && variantIds.length === qty
            ? variantIds
            : Array(qty).fill(String(this.config.defaultVariantId));

        let total = 0;
        let compare = 0;
        ids.forEach((id) => {
          total += this.unitPrice(id, planId, tier.perUnitOff);
          compare += this.comparePrice(id);
        });

        const servings = qty * (this.config.servingsPerUnit || 0);
        return {
          total,
          compare,
          savings: Math.max(0, compare - total),
          perUnit: Math.round(total / qty),
          perServing: servings ? total / servings : 0,
        };
      }

      /* ---------- rendering ---------- */

      onChange(evt) {
        // Picking a flavor inside a tier should also select that tier.
        const slot = evt.target.getAttribute?.("data-h2g-flavor");
        if (slot) {
          const qty = slot.split("-")[0];
          const tierInput = this.querySelector(
            `.h2g-bb__tier-input[value="${qty}"]`
          );
          if (tierInput && !tierInput.checked) tierInput.checked = true;
        }
        this.sync();
      }

      sync() {
        const activeQty = this.tierQty;
        const subscribing = this.planMode === "subscribe";

        const freq = this.querySelector("[data-h2g-freq-row]");
        if (freq) freq.hidden = !subscribing;

        // Every tier reprices -- the plan toggle changes the whole ladder.
        this.config.tiers.forEach((tier) => {
          const qty = Number(tier.qty);
          const el = this.querySelector(`[data-h2g-tier="${qty}"]`);
          if (!el) return;

          const ids = qty === activeQty ? this.selectedVariantIds(qty) : null;
          const t = this.totalsFor(qty, ids);

          this.setText(el, "[data-h2g-total]", this.money(t.total));

          const cmp = el.querySelector("[data-h2g-compare]");
          if (cmp) {
            cmp.hidden = t.savings <= 0;
            cmp.textContent = this.money(t.compare);
          }

          const save = el.querySelector("[data-h2g-save]");
          if (save) {
            // No "Save $0.00" -- the chip disappears when there's nothing to save.
            save.hidden = t.savings <= 0;
            save.textContent = `Save ${this.money(t.savings)}`;
          }

          if (this.config.servingsPerUnit) {
            this.setText(el, "[data-h2g-unit-day]", `${this.money(t.perServing, true)}/day`);
            this.setText(el, "[data-h2g-unit-pouch]", ` \u00b7 ${this.money(t.perUnit)} per bag`);
          } else {
            this.setText(el, "[data-h2g-unit-day]", `${this.money(t.perUnit)} per bag`);
          }
        });

        this.updatePlanNote(activeQty);
        this.updateButton(activeQty);
        this.writeCartLines(activeQty);
      }

      /**
       * The note under Subscribe & Save shows what subscribing saves at
       * the SELECTED tier -- plan discount plus volume discount -- so it
       * reads Save $10.00 on 1 bag and Save $30.00 on 2.
       */
      updatePlanNote(qty) {
        const note = this.querySelector("[data-h2g-plan-note]");
        if (!note) return;
        const subPlanId = this.querySelector("[data-h2g-frequency]")?.value;
        if (!subPlanId) return;
        const t = this.totalsFor(qty, this.selectedVariantIds(qty), String(subPlanId));
        note.textContent = t.savings > 0 ? "Save " + this.money(t.savings) : "";
      }

      /**
       * The running total lives on the theme's add-to-cart button:
       * "Preorder \u2014 $89.90". The slot span is created on demand so the
       * theme's own button markup never needs editing.
       */
      updateButton(qty) {
        if (!this.config.showTotalOnButton || !this.form) return;
        const submit = this.form.querySelector('[type="submit"]');
        if (!submit) return;

        let slot = submit.querySelector("[data-h2g-button-total]");
        if (!slot) {
          slot = document.createElement("span");
          slot.setAttribute("data-h2g-button-total", "");
          submit.appendChild(slot);
        }
        const t = this.totalsFor(qty, this.selectedVariantIds(qty));
        slot.textContent = " \u2014 " + this.money(t.total);
      }

      /* ---------- cart lines ---------- */

      /**
       * Writes items[N][...] inputs into the theme's product form and
       * disables the single-line `id`/`quantity` inputs, so one submit
       * adds every bag in the bundle.
       */
      writeCartLines(qty) {
        if (!this.form || !this.lineHolder) return;

        const planId = this.planId;
        const ids = this.selectedVariantIds(qty);

        // Merge identical bags into one line.
        const lines = [];
        ids.forEach((id) => {
          const found = lines.find((l) => l.id === id);
          if (found) found.quantity += 1;
          else lines.push({ id, quantity: 1 });
        });

        this.lineHolder.textContent = "";
        lines.forEach((line, i) => {
          this.addInput(`items[${i}][id]`, line.id);
          this.addInput(`items[${i}][quantity]`, line.quantity);
          if (planId) this.addInput(`items[${i}][selling_plan]`, planId);
          if (this.config.bundleProperty) {
            this.addInput(
              `items[${i}][properties][${this.config.bundleProperty}]`,
              `${qty}-pack`
            );
          }
        });

        // Shopify rejects a payload carrying both `id` and `items[]`, and
        // Appstle's subscription JS may inject its own selling_plan input.
        this.form
          .querySelectorAll('[name="id"], [name="quantity"], [name="selling_plan"]')
          .forEach((el) => {
            if (!el.closest("[data-h2g-lines]")) el.disabled = true;
          });
      }

      disconnectedCallback() {
        this.lineHolder?.remove();
        this.notifyForm?.remove();
        document.removeEventListener("submit", this.onDocSubmit, true);
      }

      addInput(name, value) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        this.lineHolder.appendChild(input);
      }

      /* ---------- helpers ---------- */

      setText(scope, selector, text) {
        const el = scope.querySelector(selector);
        if (el) el.textContent = text;
      }

      /** Formats cents using the shop's money_format. */
      money(cents, allowFractionalCents = false) {
        const fmt = this.config.moneyFormat || "${{amount}}";
        const value = allowFractionalCents ? cents : Math.round(cents);

        const build = (decimals, thousands, decimalSep) => {
          const n = (value / 100).toFixed(decimals);
          const [int, dec] = n.split(".");
          const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return dec ? grouped + decimalSep + dec : grouped;
        };

        return fmt.replace(
          /\{\{\s*(\w+)\s*\}\}/,
          (_, token) => {
            switch (token) {
              case "amount_no_decimals":
                return build(0, ",", ".");
              case "amount_with_comma_separator":
                return build(2, ".", ",");
              case "amount_no_decimals_with_comma_separator":
                return build(0, ".", ",");
              case "amount_with_apostrophe_separator":
                return build(2, "'", ".");
              default:
                return build(2, ",", ".");
            }
          }
        );
      }
    }
  );
}
